/**
 * Shared course utilities for the multi-block lesson content model.
 * Handles backward compatibility with the older lesson shape (video_url + description).
 */

export const THEMES = [
  { key: "apple", label: "Apple (فاخر أنيق)" },
  { key: "masterclass", label: "MasterClass (سينمائي داكن)" },
  { key: "notion", label: "Notion (نظيف بسيط)" },
];

/**
 * Convert a lesson's `blocks` array into a normalized render list.
 * Falls back to a single video block synthesized from video_url + description.
 * Returns: [{ id, type, order, ...typeSpecificFields }]
 */
export function getLessonBlocks(lesson) {
  if (!lesson) return [];
  const blocks = Array.isArray(lesson.blocks) ? lesson.blocks : [];
  if (blocks.length > 0) {
    return [...blocks].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  // Backward compatibility: synthesize from legacy fields
  const legacy = [];
  if (lesson.video_url) {
    legacy.push({
      id: `${lesson.id}-legacy-video`,
      type: "video",
      order: 0,
      video_source: guessVideoSource(lesson.video_url),
      vimeo_id: extractVimeoId(lesson.video_url),
      upload_url: lesson.video_url,
      video_title: null,
      caption: null,
    });
  }
  if (lesson.description) {
    legacy.push({
      id: `${lesson.id}-legacy-text`,
      type: "text",
      order: 1,
      content: lesson.description,
    });
  }
  return legacy;
}

/**
 * Extract the numeric Vimeo id from a Vimeo URL or an already-clean id.
 * Supports vimeo.com/76979871, player.vimeo.com/video/76979871, or raw "76979871".
 */
export function extractVimeoId(input) {
  if (!input) return null;
  const s = String(input).trim();
  if (/^\d+$/.test(s)) return s;
  const m = s.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (m) return m[1];
  return null;
}

export function guessVideoSource(url) {
  if (!url) return "upload";
  const s = String(url).toLowerCase();
  if (s.includes("vimeo.com") || /^\d+$/.test(s)) return "vimeo";
  return "upload";
}

/**
 * Build the Vimeo player embed URL for a given id (or URL).
 */
export function vimeoEmbedUrl(idOrUrl) {
  const id = extractVimeoId(idOrUrl);
  if (!id) return null;
  return `https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0&color=fb923c`;
}

/**
 * Group lessons under their section/subsection for a hierarchical view.
 * Returns: [{ id, title, subsections: [{ id, title, lessons: [] }], lessons: [] }]
 */
export function groupLessonsByHierarchy(course, allLessons) {
  const sections = Array.isArray(course?.sections) ? course.sections : [];
  // Build map: sectionId -> subsectionId -> [lessons]
  const grouped = sections.map((s) => ({
    id: s.id,
    title: s.title,
    order: s.order ?? 0,
    subsections: (s.subsections || []).map((ss) => ({
      id: ss.id,
      title: ss.title,
      order: ss.order ?? 0,
      lessons: [],
    })),
    lessons: [],
  }));

  const sectionMap = Object.fromEntries(grouped.map((s) => [s.id, s]));

  // Unassigned bucket
  const unassigned = { id: "__unassigned__", title: "بدون قسم", order: 999, subsections: [], lessons: [] };

  for (const ls of allLessons || []) {
    const sec = ls.section_id ? sectionMap[ls.section_id] : null;
    if (!sec) {
      unassigned.lessons.push(ls);
      continue;
    }
    if (ls.subsection_id) {
      const sub = sec.subsections.find((x) => x.id === ls.subsection_id);
      if (sub) sub.lessons.push(ls);
      else sec.lessons.push(ls);
    } else {
      sec.lessons.push(ls);
    }
  }

  // Sort each container by lesson.order
  const sortByOrder = (arr) => arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const s of grouped) {
    sortByOrder(s.lessons);
    for (const ss of s.subsections) sortByOrder(ss.lessons);
    sortByOrder(s.subsections);
  }
  sortByOrder(grouped);

  const result = grouped.filter((s) => s.lessons.length > 0 || s.subsections.length > 0);
  if (unassigned.lessons.length > 0) result.push(unassigned);
  return result;
}

/**
 * Flatten hierarchy into ordered lesson list for prev/next navigation.
 */
export function flattenLessons(hierarchy) {
  const out = [];
  for (const s of hierarchy || []) {
    for (const ls of s.lessons || []) out.push(ls);
    for (const ss of s.subsections || []) {
      for (const ls of ss.lessons || []) out.push(ls);
    }
  }
  return out;
}

export function newBlockId() {
  return `blk_${Math.random().toString(36).slice(2, 10)}`;
}

export function newSectionId() {
  return `sec_${Math.random().toString(36).slice(2, 10)}`;
}
